import React, { RefObject, useCallback, useEffect, useState } from 'react';
import ReactMarkdown from 'react-markdown';

import SvgCard from '../../components/svg-card/svg-card';
import { FocusableHtmlElement } from '../hooks';
import { CurrentSporsmal, isCurrentSporsmalValid } from '../reducer/current-steg';
import { CurrentSporsmalAction } from '../reducer/currentSporsmalReducer';
import { updateSporsmalSide } from '../reducer/sporsmalsflyt-actions';
import { SporsmalsflytDispatchProvider } from '../reducer/sporsmalsflytDispatchProvider';
import { Sporsmal } from '../sporsmalCard';
import { CurrentSporsmalSide, getSporsmalVisibilityStates } from './sporsmalSide-types';
import { SporsmalAnimation } from './sporsmalside-animation';

interface SporsmalSideSporsmalProps {
    sporsmal: CurrentSporsmal;
    showFeilmelding: boolean;
    onNext: () => void;
    show: boolean;
    focusOnMount: boolean;
    animate: boolean;
    errorRef?: RefObject<FocusableHtmlElement>;
}

const SporsmalSideSporsmal = ({
    sporsmal,
    showFeilmelding,
    show,
    focusOnMount,
    animate,
    onNext,
    errorRef,
}: SporsmalSideSporsmalProps) => {
    const mapAction = useCallback(
        (action: CurrentSporsmalAction) => updateSporsmalSide(action, sporsmal.sporsmal.id),
        [sporsmal.sporsmal.id],
    );

    return (
        <SporsmalsflytDispatchProvider mapAction={mapAction} key={sporsmal.sporsmal.id}>
            <SporsmalAnimation show={show} animate={animate}>
                <Sporsmal
                    currentSporsmal={sporsmal}
                    showFeilmelding={showFeilmelding}
                    onNext={onNext}
                    focusOnMount={focusOnMount}
                    errorRef={errorRef}
                />
            </SporsmalAnimation>
        </SporsmalsflytDispatchProvider>
    );
};

const findFirstSporsmalWithError = (currentSporsmal: CurrentSporsmalSide): string | undefined => {
    const sporsmalVisibilityStates = getSporsmalVisibilityStates(currentSporsmal);

    return sporsmalVisibilityStates
        .filter((visibilityState) => visibilityState.show)
        .find((visibilityState) => !isCurrentSporsmalValid(visibilityState.sporsmal))?.sporsmal?.sporsmal?.id;
};

const useAnimationState = (): boolean => {
    const [animateSporsmal, setAnimateSporsmal] = useState<boolean>(false);

    useEffect(() => {
        const timer = setTimeout(() => {
            setAnimateSporsmal(true);
        }, 0);
        return () => {
            clearTimeout(timer);
        };
    }, [setAnimateSporsmal]);

    return animateSporsmal;
};

interface Props {
    currentSporsmal: CurrentSporsmalSide;
    onNext: () => void;
    showFeilmelding: boolean;
    errorRef?: RefObject<FocusableHtmlElement>;
}

export const SporsmalSide = ({ currentSporsmal, showFeilmelding, onNext, errorRef }: Props) => {
    const animateSporsmal = useAnimationState();

    const firstSporsmalIdWithError = findFirstSporsmalWithError(currentSporsmal);

    return (
        <>
            <div className="container__standard-width sporsmal-side-tittel">
                {currentSporsmal.sporsmal.svgCard && <SvgCard type={currentSporsmal.sporsmal.svgCard} />}
                <h2 className="sporsmal-side-tittel--tittel">{currentSporsmal.sporsmal.tittel}</h2>
                {currentSporsmal.sporsmal.ingress && (
                    <div className="sporsmal-side-tittel--ingress">
                        <ReactMarkdown>{currentSporsmal.sporsmal.ingress}</ReactMarkdown>
                    </div>
                )}
            </div>
            {getSporsmalVisibilityStates(currentSporsmal).map(({ sporsmal, show }, index) => (
                <SporsmalSideSporsmal
                    sporsmal={sporsmal}
                    showFeilmelding={showFeilmelding}
                    focusOnMount={index === 0}
                    show={show}
                    animate={animateSporsmal}
                    onNext={onNext}
                    errorRef={sporsmal.sporsmal.id === firstSporsmalIdWithError ? errorRef : undefined}
                    key={sporsmal.sporsmal.id}
                />
            ))}
        </>
    );
};
